import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '@/app/providers';
import { toast } from '@/components/feedback/toast';
import { saveSettingsExtras } from '../api/settings';
import { buildDraft, type SettingsDraft, type TabId } from '../settings';
import { useHandleAvailability, useSaveSettings, type HandleStatus } from './useSettingsData';

export interface SettingsDraftState {
  draft: SettingsDraft;
  set: <K extends keyof SettingsDraft>(key: K, value: SettingsDraft[K]) => void;
  dirty: boolean;
  saving: boolean;
  activeTab: TabId;
  setActiveTab: (id: TabId) => void;
  save: () => void;
  discard: () => void;
  /** Live availability of the drafted username (shared by ProfileTab + save gate). */
  handleStatus: HandleStatus;
}

/**
 * The settings page's state machine — a clean port of the OG `_v16_settings.js`
 * draft model: a working `draft`, a `baseline` snapshot to diff against, dirty
 * detection, a tab-switch guard that offers to discard, and a save that blocks
 * on an invalid/taken username (OG `_v16Save`). DB-backed fields persist via
 * `useSaveSettings`; theme + local extras apply alongside.
 */
export function useSettingsDraft(): SettingsDraftState {
  const { user, profile } = useAuth();
  const saveMutation = useSaveSettings();

  const [activeTab, setActiveTabState] = useState<TabId>('account');
  const [draft, setDraft] = useState<SettingsDraft>(() => buildDraft(profile, user));
  const [baseline, setBaseline] = useState<SettingsDraft>(draft);
  /** The profile id the current draft was seeded from (re-seed on sign-in). */
  const seededFor = useRef<string | null>(profile?.id ?? null);

  const handleStatus = useHandleAvailability(draft.handle, user?.id);

  // Re-seed when the signed-in identity changes (null → loaded, or switch user).
  // Gated on id so a post-save profile refresh (same id) never clobbers edits.
  useEffect(() => {
    const id = profile?.id ?? null;
    if (id === seededFor.current) return;
    seededFor.current = id;
    const fresh = buildDraft(profile, user);
    setDraft(fresh);
    setBaseline(fresh);
  }, [profile, user]);

  const set = useCallback<SettingsDraftState['set']>((key, value) => {
    setDraft((d) => ({ ...d, [key]: value }));
  }, []);

  const dirty = useMemo(
    () => JSON.stringify(draft) !== JSON.stringify(baseline),
    [draft, baseline],
  );

  const discard = useCallback(() => {
    // Settings never touches the live theme — theme is owned solely by the
    // avatar-menu rail + the Locker. Discarding only reverts editable fields.
    setDraft(baseline);
  }, [baseline]);

  const setActiveTab = useCallback(
    (id: TabId) => {
      if (id === activeTab) return;
      if (dirty) {
        if (!window.confirm('You have unsaved changes. Discard them?')) return;
        discard();
      }
      setActiveTabState(id);
    },
    [activeTab, dirty, discard],
  );

  const save = useCallback(() => {
    if (handleStatus === 'invalid' || handleStatus === 'taken') {
      toast('Pick a valid, available username before saving', 'error');
      return;
    }

    if (!user) {
      // Logged-out parity: the OG kept a localStorage-only fallback.
      saveSettingsExtras(draft);
      setBaseline(draft);
      toast('✓ Settings saved', 'ok');
      return;
    }

    saveMutation.mutate(
      { id: user.id, draft },
      {
        onSuccess: () => {
          setBaseline(draft);
          toast('✓ Settings saved', 'ok');
        },
        onError: (err) => toast((err as Error).message || 'Save failed', 'error'),
      },
    );
  }, [draft, user, handleStatus, saveMutation]);

  return {
    draft,
    set,
    dirty,
    saving: saveMutation.isPending,
    activeTab,
    setActiveTab,
    save,
    discard,
    handleStatus,
  };
}
